import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { exportArtifactToPdf, type PdfExportData } from '@/utils/pdf-export';

interface PdfExportButtonProps {
  data: PdfExportData;
}

export function PdfExportButton({ data }: PdfExportButtonProps) {
  const { t } = useTranslation('common');
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    if (isExporting || !data.content) return;

    setIsExporting(true);
    try {
      await exportArtifactToPdf(data);
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error(t('pdfExport.error'));
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <button
      onClick={handleExport}
      disabled={isExporting || !data.content}
      className={`px-[24px] py-[12px] rounded-[16px] flex items-center justify-center gap-[8px] transition-colors ${
        isExporting
          ? 'bg-[#eeeeee] cursor-wait text-[#565858]'
          : 'bg-[rgba(33,151,241,0.2)] hover:bg-[#2197f1] text-[#2197f1] hover:text-white'
      }`}
    >
      {isExporting ? (
        <Loader2 className="w-[20px] h-[20px] animate-spin" strokeWidth={1.5} />
      ) : (
        <Download className="w-[20px] h-[20px]" strokeWidth={1.5} />
      )}
      <span className="font-segoe font-semibold text-[14px] leading-[20px] text-current">
        {isExporting ? t('pdfExport.exporting') : t('pdfExport.download')}
      </span>
    </button>
  );
}
